import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
    Accordion, AccordionTab, Avatar, Button,
    ButtonSize, ButtonVariant, Iconify, PageLoader, SocialMediaLinks
} from "@repo/design-system/components/ui";
import StudentProfile from "@gyandiksha/profile/components/StudentProfile";
import AddressPreviewCard from "@gyandiksha/profile/components/common/AddressPreviewCard";
import { useLazyGetCompanyByIdQuery } from "@/app/services/companyAPI";
import "./Profile.scss";

const getDisplayValue = (value) => {
    if (!value) return "-";
    if (typeof value !== "object") return value;
    if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "-";
    return value.value ?? value.label ?? "-";
};

const getLinkUrl = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

// Plain social URLs -> [{ name, url }] for the shared SocialMediaLinks — the platform name (which picks the icon)
// is the site's domain name, e.g. www.linkedin.com -> linkedin
const getSocialNetwork = (links = []) => links.filter(Boolean).map((url) => {
    let name = "website";
    try {
        name = new URL(getLinkUrl(url)).hostname.replace(/^www\./, "").split(".")[0];
    } catch {
        // Not a parsable URL — keep the generic globe icon
    }
    return { name, url };
});

const InfoField = ({ label, value }) => (
    <div className="pp-info-field">
        <span className="pp-info-label">{label}</span>
        <span className="pp-info-value">{getDisplayValue(value)}</span>
    </div>
);

const Profile = () => {

    const navigate = useNavigate();
    const { companyId } = useSelector((state) => state.auth);
    const [expandedSections, setExpandedSections] = useState({
        companyInfo: true,
        address: false,
    });

    const [fetchCompany, { data: companyData, isLoading }] = useLazyGetCompanyByIdQuery();

    useEffect(() => {
        if (companyId) {
            fetchCompany(companyId);
        }
    }, [companyId, fetchCompany]);

    const company = companyData?.data;

    const toggleSection = (key) =>
        setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

    const handleEdit = () => {
        navigate("/profile/edit-company");
    };

    if (!companyId) {
        return <StudentProfile onEditProfile={() => navigate("/edit-profile")} />;
    }

    return (
        <div className="placement-profile">
            {isLoading ? <PageLoader /> : (
                <>
                    {/* ── Header Banner ── */}
                    <div className="pp-banner">
                        <div className="pp-banner-icon">
                            <Iconify icon="solar:buildings-3-bold-duotone" width={28} />
                        </div>
                        <div className="pp-banner-info">
                            <h3>Company Profile</h3>
                            <p>View and manage your company information.</p>
                        </div>
                        <Button variant={ButtonVariant.PRIMARY} size={ButtonSize.SM}
                            leftIcon="lucide:pencil" className="pp-edit-btn" onClick={handleEdit}>
                            Edit Profile
                        </Button>
                    </div>

                    <div className="pp-content row pl-3 pr-0">
                        {/* ── Left Panel ── */}
                        <div className="pp-left-panel col-md-5 col-xl-4">
                            <div className="pp-avatar-wrapper">
                                <Avatar src={company?.companyLogo} alt={company?.name} size="large" shape="circle" />
                            </div>

                            <h2 className="pp-name">{company?.name || "-"}</h2>

                            <div className="pp-details mt-3">
                                <div className="pp-detail-row">
                                    <span className="pp-detail-label">Company Type :</span>
                                    <span className="pp-detail-value">{getDisplayValue(company?.companyType)}</span>
                                </div>
                                <div className="pp-divider" />
                                <div className="pp-detail-row">
                                    <span className="pp-detail-label">Industry / Sector :</span>
                                    <span className="pp-detail-value">{getDisplayValue(company?.industrySector)}</span>
                                </div>
                                <div className="pp-detail-row">
                                    <span className="pp-detail-label">Company Website :</span>
                                    <span className="pp-detail-value">
                                        {company?.companyWebsite
                                            ? <a className="pp-link" href={getLinkUrl(company.companyWebsite)}
                                                target="_blank" rel="noopener noreferrer">{company.companyWebsite}</a>
                                            : "-"}
                                    </span>
                                </div>
                                <div className="pp-detail-row">
                                    <span className="pp-detail-label">Email :</span>
                                    <span className="pp-detail-value">{company?.email || "-"}</span>
                                </div>
                                <div className="pp-detail-row">
                                    <span className="pp-detail-label">Contact Number :</span>
                                    <span className="pp-detail-value">{company?.contact || "-"}</span>
                                </div>
                                <div className="pp-detail-row">
                                    <span className="pp-detail-label">Alt. Email :</span>
                                    <span className="pp-detail-value">{company?.altEmail || "-"}</span>
                                </div>
                                <div className="pp-detail-row">
                                    <span className="pp-detail-label">Alt. Contact Number :</span>
                                    <span className="pp-detail-value">{company?.altContact || "-"}</span>
                                </div>
                                <div className="pp-detail-row">
                                    <span className="pp-detail-label">Contact Person :</span>
                                    <span className="pp-detail-value">{company?.contactPersonName || "-"}</span>
                                </div>
                                <div className="pp-detail-row">
                                    <span className="pp-detail-label">Social Media :</span>
                                    <span className="pp-detail-value">
                                        {company?.socialMediaLinks?.some(Boolean)
                                            ? <SocialMediaLinks socialNetwork={getSocialNetwork(company.socialMediaLinks)}
                                                iconVariant="color" iconSize={20} />
                                            : "-"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* ── Right Panel ── */}
                        <div className="pp-right-panel col-md-7 col-xl-9">
                            <Accordion activeIndex={expandedSections.companyInfo ? 0 : null}
                                onTabChange={() => toggleSection("companyInfo")}>
                                <AccordionTab header={
                                    <div className="flex align-items-center gap-2">
                                        <Iconify icon="lucide:building-2" width={16} />
                                        <span>Company Information</span>
                                    </div>
                                }>
                                    <div className="pp-info-grid">
                                        <InfoField label="PAN Number :" value={company?.panNo} />
                                        <InfoField label="CIN Number :" value={company?.cinNo} />
                                        <InfoField label="TAN Number :" value={company?.tanNo} />
                                    </div>
                                    <div className="pp-description-block">
                                        <p className="pp-description-title">Description</p>
                                        <p className="pp-description-text">
                                            {company?.companyDescription || "No description provided."}
                                        </p>
                                    </div>
                                </AccordionTab>
                            </Accordion>

                            <Accordion activeIndex={expandedSections.address ? 0 : null}
                                onTabChange={() => toggleSection("address")}>
                                <AccordionTab header={
                                    <div className="flex align-items-center gap-2">
                                        <Iconify icon="lucide:map-pin" width={16} />
                                        <span>Address Details</span>
                                    </div>
                                }>
                                    <div className="pp-address-block">
                                        <p className="pp-address-title">Head Office</p>
                                        <AddressPreviewCard isBackGroundStyle
                                            address={company?.address}
                                            districtName={company?.districtOption?.value}
                                            stateName={company?.stateOption?.value}
                                            nationality={company?.countryOption?.value}
                                        />
                                    </div>

                                    {company?.otherBranches?.length > 0 &&
                                        <div className="pp-address-block">
                                            <p className="pp-address-title">Other Branch Address</p>
                                            <div className="pp-branch-grid">
                                                {company.otherBranches.map((branch, index) => (
                                                    <div key={`${branch.name}-${index}`} className="pp-branch-card">
                                                        {branch.name && <p className="pp-branch-name">{branch.name}</p>}
                                                        <AddressPreviewCard
                                                            address={branch.address}
                                                            districtName={branch.districtOption?.value}
                                                            stateName={branch.stateOption?.value}
                                                            nationality={branch.countryOption?.value}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    }
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Profile;
